import React, {
  CSSProperties,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { NumericFormat } from "react-number-format";
import { scroller } from "react-scroll";
import { FieldHandles, ValueSubmitTrigger } from "types";
import useStyles from "./styles";

type TableNumberFieldProps = {
  value: number | "";
  onValueChange: (value: number | "") => void;
  submitValue: (value: number | "", trigger: ValueSubmitTrigger) => void;
  id: string;
  autoFocus?: boolean;
  disabled?: boolean;
  decimalScale?: number;
  textAlign?: CSSProperties["textAlign"];
};

const TableNumberFieldComponent: React.ForwardRefRenderFunction<
  FieldHandles,
  TableNumberFieldProps
> = (
  {
    value,
    onValueChange,
    submitValue,
    id,
    autoFocus = false,
    disabled = false,
    decimalScale,
    textAlign = "left",
  },
  tableNumberFieldRef,
) => {
  const { classes, cx } = useStyles({ textAlign });

  const [inputStatus, setInputStatus] = useState<
    "success" | "error" | "default"
  >("default");

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(tableNumberFieldRef, () => ({
    highlight: (scroll = true) => {
      setInputStatus("error");
      if (scroll) {
        scroller.scrollTo(id, {
          smooth: true,
          offset: -150,
          duration: 700,
        });
      }
      inputRef.current?.focus();
      inputRef.current?.select();
    },
  }));

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const triggerMapping: { [key: string]: ValueSubmitTrigger } = {
      Enter: ValueSubmitTrigger.Enter,
      Escape: ValueSubmitTrigger.Escape,
      Tab: event.shiftKey
        ? ValueSubmitTrigger.ShiftTab
        : ValueSubmitTrigger.Tab,
      ArrowUp: ValueSubmitTrigger.ArrowUp,
      ArrowDown: ValueSubmitTrigger.ArrowDown,
      ArrowLeft: ValueSubmitTrigger.ArrowLeft,
      ArrowRight: ValueSubmitTrigger.ArrowRight,
    };

    if (triggerMapping[event.key]) {
      event.preventDefault();
      event.stopPropagation();
      submitValue(value, triggerMapping[event.key]);
    }
  };

  return (
    <NumericFormat
      id={id}
      name={id}
      value={value}
      onValueChange={({ formattedValue, floatValue }) => {
        if (formattedValue === "," || formattedValue === "0,") return;
        onValueChange(floatValue ?? "");
      }}
      thousandSeparator="."
      decimalSeparator=","
      getInputRef={inputRef}
      decimalScale={decimalScale}
      fixedDecimalScale={false}
      autoFocus={autoFocus}
      onBlur={() => submitValue(value, ValueSubmitTrigger.Blur)}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={cx(classes.input, {
        [classes.error]: inputStatus === "error",
        [classes.success]: inputStatus === "success",
      })}
    />
  );
};

export const TableNumberField = React.forwardRef(TableNumberFieldComponent);
